import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Pipette } from 'lucide-react';

// A full colour picker — saturation/value field, hue slider, hex field — for
// the places that used to offer eight fixed swatches and nothing else.
//
// Why this is hand-rolled rather than a dependency: the control has to paint
// from `--co-*` tokens to survive a theme swap, and every off-the-shelf picker
// ships its own stylesheet in its own palette. The colour maths below is the
// whole of what a library would have provided.
//
// The panel expands IN FLOW rather than floating. Every screen that hosts this
// picker is a modal with a scrolling body, and an absolutely-positioned panel
// is clipped by that scroller exactly when the field sits near the bottom —
// which is where this field sits on the ticket-type form.

export interface ColorOption {
  value: string;
  label: string;
}

export interface Hsv {
  /** 0–360 */
  h: number;
  /** 0–1 */
  s: number;
  /** 0–1 */
  v: number;
}

/** `#abc` / `ABCDEF` / `#abcdef` → `#abcdef`. Anything else → null. */
export function normalizeHex(input: string): string | null {
  const body = input.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(body)) {
    return `#${body
      .split('')
      .map((c) => c + c)
      .join('')
      .toLowerCase()}`;
  }
  if (/^[0-9a-f]{6}$/i.test(body)) return `#${body.toLowerCase()}`;
  return null;
}

export function hsvToHex({ h, s, v }: Hsv): string {
  const c = v * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const [r1, g1, b1] =
    hp < 1
      ? [c, x, 0]
      : hp < 2
        ? [x, c, 0]
        : hp < 3
          ? [0, c, x]
          : hp < 4
            ? [0, x, c]
            : hp < 5
              ? [x, 0, c]
              : [c, 0, x];
  const m = v - c;
  const byte = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${byte(r1)}${byte(g1)}${byte(b1)}`;
}

/**
 * Hue is undefined for greys and black, and the maths would return 0 (red).
 * Returning null for `h` there lets the caller KEEP the hue the user last
 * picked — otherwise dragging value down to black silently resets the slider
 * to red, and dragging back up returns a different colour than the one left.
 */
export function hexToHsv(hex: string): (Hsv & { h: number | null }) | null {
  const norm = normalizeHex(hex);
  if (!norm) return null;
  const r = parseInt(norm.slice(1, 3), 16) / 255;
  const g = parseInt(norm.slice(3, 5), 16) / 255;
  const b = parseInt(norm.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h: number | null = null;
  if (d !== 0) {
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
    h = (h + 360) % 360;
  }
  return { h: h as number, s: max === 0 ? 0 : d / max, v: max };
}

/** White or near-white needs dark ink on top of it, and vice versa. */
export function readableInk(hex: string): string {
  const norm = normalizeHex(hex) ?? '#000000';
  const r = parseInt(norm.slice(1, 3), 16);
  const g = parseInt(norm.slice(3, 5), 16);
  const b = parseInt(norm.slice(5, 7), 16);
  // Rec. 709 luma — good enough to decide black vs white ink.
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 150 ? '#111111' : '#ffffff';
}

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n);

export interface ColorPickerProps {
  /** Current colour as a hex string. */
  value: string;
  /** Called with a normalised `#rrggbb` on every change, including drags. */
  onChange: (hex: string) => void;
  /** The quick row above the picker. Omit for no presets. */
  presets?: ColorOption[];
  /** Accessible name for the control, e.g. "Ticket type colour". */
  label?: string;
  className?: string;
}

export function ColorPicker({
  value,
  onChange,
  presets = [],
  label = 'Colour',
  className = '',
}: ColorPickerProps) {
  const current = normalizeHex(value) ?? '#000000';
  const isPreset = presets.some((p) => normalizeHex(p.value) === current);

  // Open by default when the stored colour is NOT one of the presets: an
  // edit form has to show where its colour came from, or the custom value
  // looks like nothing is selected.
  const [open, setOpen] = useState(!isPreset && Boolean(normalizeHex(value)));
  const [hsv, setHsv] = useState<Hsv>(() => {
    const parsed = hexToHsv(current);
    return { h: parsed?.h ?? 0, s: parsed?.s ?? 0, v: parsed?.v ?? 0 };
  });
  const [hexText, setHexText] = useState(current);

  const svRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const mounted = useRef(false);

  /**
   * Expanding in flow avoids being CLIPPED by the host modal's scrolling body,
   * but it can still open below its fold — the field sits low on the
   * ticket-type form, and the hex box landed under the edge. Bring the panel
   * into view when the user opens it (not on mount, which would yank a modal
   * the moment it appears).
   */
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    if (!open) return;
    const el = panelRef.current;
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [open]);

  // Re-sync only when the incoming colour is not the one this picker just
  // produced — otherwise every drag frame would fight the local hue.
  useEffect(() => {
    setHexText(current);
    setHsv((prev) => {
      if (hsvToHex(prev) === current) return prev;
      const parsed = hexToHsv(current);
      if (!parsed) return prev;
      return { h: parsed.h ?? prev.h, s: parsed.s, v: parsed.v };
    });
  }, [current]);

  const commit = useCallback(
    (next: Hsv) => {
      setHsv(next);
      const hex = hsvToHex(next);
      setHexText(hex);
      onChange(hex);
    },
    [onChange],
  );

  const readPoint = (el: HTMLElement, clientX: number, clientY: number) => {
    const r = el.getBoundingClientRect();
    // jsdom reports a zero-sized rect; guard so tests don't divide by zero.
    return {
      x: r.width ? clamp01((clientX - r.left) / r.width) : 0,
      y: r.height ? clamp01((clientY - r.top) / r.height) : 0,
    };
  };

  const applySv = (clientX: number, clientY: number) => {
    const el = svRef.current;
    if (!el) return;
    const { x, y } = readPoint(el, clientX, clientY);
    commit({ h: hsv.h, s: x, v: 1 - y });
  };

  const applyHue = (clientX: number) => {
    const el = hueRef.current;
    if (!el) return;
    const { x } = readPoint(el, clientX, 0);
    commit({ ...hsv, h: x * 360 });
  };

  const startDrag = (
    e: React.PointerEvent<HTMLDivElement>,
    apply: (x: number, y: number) => void,
  ) => {
    const el = e.currentTarget;
    if (typeof el.setPointerCapture === 'function') el.setPointerCapture(e.pointerId);
    apply(e.clientX, e.clientY);
  };

  const onDragMove = (
    e: React.PointerEvent<HTMLDivElement>,
    apply: (x: number, y: number) => void,
  ) => {
    // `buttons` is the only reliable "is the button still down" across a
    // captured drag that left the element.
    if (e.buttons !== 1) return;
    apply(e.clientX, e.clientY);
  };

  const onHexCommit = () => {
    const norm = normalizeHex(hexText);
    if (!norm) {
      setHexText(current); // Invalid text reverts rather than clearing the colour.
      return;
    }
    const parsed = hexToHsv(norm);
    setHsv({ h: parsed?.h ?? hsv.h, s: parsed?.s ?? 0, v: parsed?.v ?? 0 });
    setHexText(norm);
    onChange(norm);
  };

  const hueHex = hsvToHex({ h: hsv.h, s: 1, v: 1 });

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2">
        {presets.map((preset) => {
          const hex = normalizeHex(preset.value) ?? preset.value;
          const on = hex === current;
          return (
            <button
              key={preset.value}
              type="button"
              onClick={() => {
                const parsed = hexToHsv(hex);
                setHsv({ h: parsed?.h ?? hsv.h, s: parsed?.s ?? 0, v: parsed?.v ?? 0 });
                setHexText(hex);
                onChange(hex);
              }}
              aria-pressed={on}
              title={preset.label}
              aria-label={preset.label}
              className="flex h-8 w-8 items-center justify-center rounded-full border-2 transition-transform hover:scale-105"
              style={{
                backgroundColor: hex,
                borderColor: on ? 'var(--co-ink)' : 'transparent',
              }}
            >
              {on && <Check className="h-4 w-4" strokeWidth={3} style={{ color: readableInk(hex) }} />}
            </button>
          );
        })}

        {/* The way out of the eight swatches. It shows the live colour and its
            hex, so the row still answers "what is selected" when the answer is
            not one of the presets. */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={`${label}: custom`}
          className="flex h-8 items-center gap-2 rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-2 text-[12px] text-[var(--co-ink-2)] hover:border-[var(--co-brass)]"
        >
          <Pipette className="h-[13px] w-[13px]" />
          <span className="font-mono uppercase">{current}</span>
          <span
            className="h-4 w-4 flex-none rounded border border-[var(--co-line)]"
            style={{ backgroundColor: current }}
            aria-hidden="true"
          />
        </button>
      </div>

      {open && (
        <div
          ref={panelRef}
          className="mt-3 w-[236px] rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] p-3"
          style={{ boxShadow: 'var(--co-shadow-card)' }}
        >
          {/* Saturation (x) against value (y), over the current hue. */}
          <div
            ref={svRef}
            role="slider"
            tabIndex={0}
            aria-label={`${label}: saturation and brightness`}
            aria-valuetext={`Saturation ${Math.round(hsv.s * 100)}%, brightness ${Math.round(hsv.v * 100)}%`}
            aria-valuenow={Math.round(hsv.v * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            onPointerDown={(e) => startDrag(e, applySv)}
            onPointerMove={(e) => onDragMove(e, applySv)}
            onKeyDown={(e) => {
              const step = e.shiftKey ? 0.1 : 0.02;
              if (e.key === 'ArrowRight') commit({ ...hsv, s: clamp01(hsv.s + step) });
              else if (e.key === 'ArrowLeft') commit({ ...hsv, s: clamp01(hsv.s - step) });
              else if (e.key === 'ArrowUp') commit({ ...hsv, v: clamp01(hsv.v + step) });
              else if (e.key === 'ArrowDown') commit({ ...hsv, v: clamp01(hsv.v - step) });
              else return;
              e.preventDefault();
            }}
            className="relative h-[132px] w-full cursor-crosshair touch-none rounded-md"
            style={{
              background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueHex})`,
            }}
          >
            <span
              className="pointer-events-none absolute h-[14px] w-[14px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white"
              style={{
                left: `${hsv.s * 100}%`,
                top: `${(1 - hsv.v) * 100}%`,
                backgroundColor: hsvToHex(hsv),
                boxShadow: '0 0 0 1px rgba(0,0,0,.35)',
              }}
              aria-hidden="true"
            />
          </div>

          {/* Hue. */}
          <div
            ref={hueRef}
            role="slider"
            tabIndex={0}
            aria-label={`${label}: hue`}
            aria-valuenow={Math.round(hsv.h)}
            aria-valuemin={0}
            aria-valuemax={360}
            onPointerDown={(e) => startDrag(e, (x) => applyHue(x))}
            onPointerMove={(e) => onDragMove(e, (x) => applyHue(x))}
            onKeyDown={(e) => {
              const step = e.shiftKey ? 20 : 4;
              if (e.key === 'ArrowRight') commit({ ...hsv, h: (hsv.h + step) % 360 });
              else if (e.key === 'ArrowLeft') commit({ ...hsv, h: (hsv.h - step + 360) % 360 });
              else return;
              e.preventDefault();
            }}
            className="relative mt-3 h-3 w-full cursor-pointer touch-none rounded-full"
            style={{
              background:
                'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
            }}
          >
            <span
              className="pointer-events-none absolute top-1/2 h-[16px] w-[16px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white"
              style={{
                left: `${(hsv.h / 360) * 100}%`,
                backgroundColor: hueHex,
                boxShadow: '0 0 0 1px rgba(0,0,0,.35)',
              }}
              aria-hidden="true"
            />
          </div>

          {/* Hex — typed, pasted from anywhere, and the only part of this panel
              a keyboard user has to reach to set an exact brand colour. */}
          <div className="mt-3 flex items-center gap-2">
            <span
              className="h-[30px] w-[30px] flex-none rounded-md border border-[var(--co-line)]"
              style={{ backgroundColor: current }}
              aria-hidden="true"
            />
            <label className="flex-1">
              <span className="sr-only">{`${label}: hex value`}</span>
              <input
                type="text"
                value={hexText}
                spellCheck={false}
                onChange={(e) => setHexText(e.target.value)}
                onBlur={onHexCommit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onHexCommit();
                  }
                }}
                className="h-[30px] w-full rounded-md border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-2 font-mono text-[12.5px] uppercase text-[var(--co-ink)] outline-none focus:border-[var(--co-brass)]"
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
